// ---------------------------------------------------------------------------
// usePatternTier
// ---------------------------------------------------------------------------
// Computes which "AI maturity tier" the current user has earned based on how
// many of their sessions have at least one logged catch. Tier gates Pattern
// Insight (and, soon, Preparation Score) so we never call an LLM with so
// little data that we end up surfacing hallucinated patterns.
//
// Tier ladder (catch-sessions = sessions where catchCount >= 1):
//   Tier 0  Empty           0          → no AI, "log your first trip" CTA
//   Tier 1  Seeding         1–4        → no AI, local descriptive stats
//   Tier 2  Early Read      5–9        → AI w/ low-confidence framing
//   Tier 3  Active          10–19      → full AI Pattern Insight
//   Tier 4  Compound        20+        → Pattern Insight + compound + Best Window
//
// Why catch-sessions and not raw session count: 20 skunked trips at the same
// tide stage tell the model nothing. A "real" session for pattern-learning
// purposes is one that produced data points the model can correlate against.
//
// Caching: Firestore counts (getCountFromServer) are cheap but not free —
// 1 billable read per session per refresh. We cache the resulting tier in
// localStorage for 24h per user. The cache is invalidated automatically the
// next time the hook mounts after the TTL expires, so a user who crosses a
// threshold will see the new tier within a day (or immediately if they sign
// out / back in). For instant feedback on ship-day we expose a `refresh()`
// callback so future surfaces (e.g. after END TRIP) can force a recount.
// ---------------------------------------------------------------------------

import { useCallback, useEffect, useRef, useState } from "react";
import { collection, getCountFromServer, getDocs } from "firebase/firestore";
import { db } from "../firebase";

// Tier thresholds — keep in sync with docs/instructional-video-script.md and
// the Pattern Insight section of DashboardScreen.jsx.
export const TIER_THRESHOLDS = [0, 1, 5, 10, 20];
export const TIER_LABELS = ["EMPTY", "SEEDING", "EARLY READ", "ACTIVE", "COMPOUND"];

// v1 ship: Tier 2 "Early Read" surfaces as Tier 1 "Seeding" UX to avoid
// shipping low-confidence AI copy before we have polished framing for it.
// Flip to true once the Tier 2 banner copy and confidence cap are reviewed.
export const SHIP_TIER_2 = false;

const CACHE_TTL_MS = 24 * 60 * 60 * 1000; // 24h
const CACHE_KEY = (uid) => `the99.patternTier.${uid}`;

function readCache(uid) {
  if (!uid || typeof window === "undefined") return null;
  try {
    const raw = window.localStorage.getItem(CACHE_KEY(uid));
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== "object") return null;
    if (!parsed.computedAt || Date.now() - parsed.computedAt > CACHE_TTL_MS) return null;
    return parsed;
  } catch {
    return null;
  }
}

function writeCache(uid, payload) {
  if (!uid || typeof window === "undefined") return;
  try {
    window.localStorage.setItem(
      CACHE_KEY(uid),
      JSON.stringify({ ...payload, computedAt: Date.now() })
    );
  } catch {
    /* quota / private mode — non-fatal */
  }
}

// Invalidate the cached tier so the next mount of usePatternTier recomputes
// from Firestore. Call this whenever the underlying catch data changes (e.g.
// after logging a catch or ending a trip) so the progress pill updates without
// waiting out the 24h TTL.
export function invalidatePatternTierCache(uid) {
  if (!uid || typeof window === "undefined") return;
  try {
    window.localStorage.removeItem(CACHE_KEY(uid));
  } catch {
    /* private mode — non-fatal */
  }
}

function tierForCatchSessions(catchSessions) {
  if (catchSessions >= 20) return 4;
  if (catchSessions >= 10) return 3;
  if (catchSessions >= 5) return 2;
  if (catchSessions >= 1) return 1;
  return 0;
}

function nextThresholdForTier(tier) {
  // Returns the catch-session count needed to advance one tier, or null if
  // already at the top.
  const next = TIER_THRESHOLDS[tier + 1];
  return typeof next === "number" ? next : null;
}

export default function usePatternTier(userId) {
  const [state, setState] = useState(() => {
    const cached = userId ? readCache(userId) : null;
    if (cached) {
      const tier = tierForCatchSessions(cached.catchSessions);
      return {
        tier,
        catchSessions: cached.catchSessions,
        totalSessions: cached.totalSessions,
        nextThreshold: nextThresholdForTier(tier),
        sessionsToNextTier:
          nextThresholdForTier(tier) != null
            ? Math.max(0, nextThresholdForTier(tier) - cached.catchSessions)
            : 0,
        label: TIER_LABELS[tier],
        loading: false,
        fromCache: true,
      };
    }
    return {
      tier: 0,
      catchSessions: 0,
      totalSessions: 0,
      nextThreshold: TIER_THRESHOLDS[1],
      sessionsToNextTier: TIER_THRESHOLDS[1],
      label: TIER_LABELS[0],
      loading: !!userId,
      fromCache: false,
    };
  });

  const inFlightUidRef = useRef(null);

  const recount = useCallback(async (uid) => {
    if (!uid) return;
    inFlightUidRef.current = uid;
    try {
      const sessionsSnap = await getDocs(collection(db, "users", uid, "sessions"));
      if (inFlightUidRef.current !== uid) return; // user changed mid-flight
      const sessionDocs = sessionsSnap.docs;
      const totalSessions = sessionDocs.length;

      // Count catches per session in parallel. getCountFromServer is the
      // cheapest path — one billable read per call regardless of how many
      // catches exist in the subcollection.
      const counts = await Promise.all(
        sessionDocs.map(async (s) => {
          try {
            const c = await getCountFromServer(
              collection(db, "users", uid, "sessions", s.id, "catches")
            );
            return c.data().count || 0;
          } catch {
            return 0;
          }
        })
      );

      if (inFlightUidRef.current !== uid) return;
      const catchSessions = counts.reduce((acc, n) => acc + (n > 0 ? 1 : 0), 0);
      const tier = tierForCatchSessions(catchSessions);
      const next = nextThresholdForTier(tier);

      const nextState = {
        tier,
        catchSessions,
        totalSessions,
        nextThreshold: next,
        sessionsToNextTier: next != null ? Math.max(0, next - catchSessions) : 0,
        label: TIER_LABELS[tier],
        loading: false,
        fromCache: false,
      };
      setState(nextState);
      writeCache(uid, { catchSessions, totalSessions });
    } catch (err) {
      console.warn("[usePatternTier] recount failed:", err);
      setState((prev) => ({ ...prev, loading: false }));
    }
  }, []);

  useEffect(() => {
    if (!userId) {
      inFlightUidRef.current = null;
      setState({
        tier: 0,
        catchSessions: 0,
        totalSessions: 0,
        nextThreshold: TIER_THRESHOLDS[1],
        sessionsToNextTier: TIER_THRESHOLDS[1],
        label: TIER_LABELS[0],
        loading: false,
        fromCache: false,
      });
      return;
    }
    const cached = readCache(userId);
    if (cached) {
      // Already hydrated from cache in initial state. Don't refetch.
      return;
    }
    setState((prev) => ({ ...prev, loading: true }));
    recount(userId);
  }, [userId, recount]);

  const refresh = useCallback(() => {
    if (!userId) return;
    setState((prev) => ({ ...prev, loading: true }));
    recount(userId);
  }, [userId, recount]);

  // Resolve the effective tier we should *render*. If SHIP_TIER_2 is off,
  // collapse Tier 2 down to Tier 1 so callers can branch on a single value
  // without scattering the feature flag throughout the UI.
  const effectiveTier = !SHIP_TIER_2 && state.tier === 2 ? 1 : state.tier;

  return {
    ...state,
    effectiveTier,
    refresh,
  };
}
